import { neutral, orange, purple } from "@flat-ui";

/**
 * Determines the hexadecimal decimal value for a given number in decimal.
 * Useful in transparencies and other color props where we convert to HEX
 * values ranging from 0 to 255.
 * The key is the number in decimal and the value the conversion to hexadecimal.
 */
export enum EDecimalToHex {
  zero = "00",
  sixteen = "10",
  twenty = "14",
  twentySix = "1A",
  thirtyTwo = "20",
  thirtyEight = "26",
  forty = "28",
  fiftyOne = "33",
  sixtyFour = "40",
  seventy = "46",
  seventySeven = "4D",
  hundredTwo = "66",
  hundredTwentyEight = "80",
  hundredFortyFour = "90",
  hundredFiftyThree = "99",
  hundredSixtySix = "A6",
  twoHundredThree = "CB",
  twoHundredFour = "CC",
}

/** Defines some constants in the colors named by how are they used. */
export const colorConst = {
  /** Used for placeholder  */
  placeholder: neutral[500],

  normalFont: neutral[800],

  /** Background color for the tag and badge to indicate that something is new */
  newFeatureIndicator: orange[500],

  /** Font/background color for dev features which aren't visible to regular users */
  devFeature: purple[500],
};

/**
 * Generate a color with a specified alpha value that equals the hexColor when overlaid on a white background.
 * Note: This can not work for all colors in principle.
 */
export function generateWhiteOverlayColor(
  hexColor: string,
  alpha: number
): string {
  const MAX_BYTE = 255;
  const RADIX_HEX = 16;

  if (hexColor.startsWith("#")) {
    hexColor = hexColor.slice(1);
  }
  // Parse the hex color into RGB components (in decimal).
  // 0, 2, 4 are the starting indices for R, G, B in the hex string.1
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  const [r, g, b] = [0, 2, 4].map((start) =>
    parseInt(hexColor.slice(start, start + 2), RADIX_HEX)
  );

  const [blendedR, blendedG, blendedB] = [r, g, b].map(
    (channel: number): number => {
      // Example channel: 240, alpha: 0.5:
      // 240 - 255 * (1 - 0.5) / 0.5 = 225
      // 225 blended on 255 => (225 + 255) * 0.5 = 480 * 0.5 = 240
      const newCol = Math.round((channel - MAX_BYTE * (1 - alpha)) / alpha);
      // Ensure the value is in the range [0, 255]
      return Math.min(Math.max(newCol, 0), MAX_BYTE);
    }
  );
  return `rgba(${blendedR}, ${blendedG}, ${blendedB}, ${alpha})`;
}

/* eslint-disable @typescript-eslint/no-magic-numbers */
/* Transparent versions of sphereColors that have the same color on a white background */
export const sphereColorsTransparent = {
  gray50: generateWhiteOverlayColor(neutral[50], 0.05),
  gray200: generateWhiteOverlayColor(neutral[200], 0.2),
  gray600: generateWhiteOverlayColor(neutral[600], 0.6),
  gray800: generateWhiteOverlayColor(neutral[800], 0.8),
};
/* eslint-enable @typescript-eslint/no-magic-numbers */
